import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useApp } from '../store/app';
import { NAV_SECTIONS, VIEW_TITLES } from './nav';
import { Icon } from '../components/Icon';
import { Avatar } from '../components/primitives';
import { canSeeView, ROLE_LABELS } from '../lib/rbac';
import { initials, kindFor } from '../lib/avatar';
import { DrawerHost } from './DrawerHost';
import { HeaderSearch } from './HeaderSearch';
import { NotificationsBell } from './NotificationsBell';
import type { Role } from '../../shared/types';

export function Layout() {
  const { dark, toggleDark, collapsed, toggleCollapse, user, loadingUser } = useApp();
  const loc = useLocation();
  const nav = useNavigate();

  // Don't silently fall back to a staff view when we couldn't identify the
  // caller — that's misleading. Show an explicit state instead.
  if (loadingUser) return <Centered>Loading…</Centered>;
  if (!user) return <AuthError />;

  const view = loc.pathname.split('/')[1] || 'dashboard';
  const role: Role = user.role;
  const [title, sub] = VIEW_TITLES[view] || ['', ''];
  const dateStr = new Intl.DateTimeFormat('en-US', { weekday: 'long', month: 'long', day: 'numeric' }).format(new Date());
  const subtitle = view === 'dashboard' ? `${dateStr} — ${sub}` : sub;

  const sidebarW = collapsed ? 72 : 248;
  const showLabels = !collapsed;
  const avInitials = user ? initials(user.name) : 'CC';
  const avKind = user?.avatar_kind || kindFor(user?.email || 'cc');

  const NavBtn = ({ item }: { item: { id: string; path: string; label: string; icon: string } }) => {
    const active = view === item.id;
    return (
      <button
        onClick={() => nav(item.path)}
        title={item.label}
        className="cc-hover-s2"
        style={{
          appearance: 'none',
          border: 0,
          cursor: 'pointer',
          textAlign: 'left',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 11,
          padding: '9px 10px',
          borderRadius: 8,
          background: active ? 'var(--accent-bg)' : 'transparent',
          color: active ? 'var(--accent)' : 'var(--text-2)',
          fontFamily: 'inherit',
          fontSize: 13.5,
          fontWeight: active ? 600 : 500,
          whiteSpace: 'nowrap',
        }}
      >
        <Icon name={item.icon} size={21} fill={active ? 1 : 0} />
        {showLabels && <span>{item.label}</span>}
      </button>
    );
  };

  return (
    <div style={{ display: 'flex', height: '100vh', width: '100%', overflow: 'hidden', background: 'var(--bg)', color: 'var(--text)' }}>
      {/* Sidebar */}
      <aside style={{ width: sidebarW, flex: '0 0 auto', background: 'var(--surface)', borderRight: '1px solid var(--border)', display: 'flex', flexDirection: 'column', transition: 'width .18s ease', overflow: 'hidden' }}>
        <div style={{ height: 56, flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: 8, padding: '0 14px 0 18px', borderBottom: '1px solid var(--border)' }}>
          {showLabels && (
            <div style={{ fontWeight: 700, fontSize: 17, letterSpacing: '-.02em', flex: '1 1 auto' }}>CCSilo</div>
          )}
          <button onClick={toggleCollapse} title="Toggle sidebar" className="cc-hover-s2" style={{ appearance: 'none', border: 0, background: 'transparent', cursor: 'pointer', color: 'var(--text-2)', width: 34, height: 34, flex: '0 0 auto', margin: '0 auto', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon name="menu" size={22} />
          </button>
        </div>
        <nav style={{ flex: '1 1 auto', overflowY: 'auto', padding: 10, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {NAV_SECTIONS.map((section, si) => {
            const items = section.items.filter((i) => canSeeView(role, i.id));
            if (items.length === 0) return null;
            return (
              <div key={section.label || `s${si}`} style={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: si === 0 ? 0 : 12 }}>
                {section.label && showLabels && (
                  <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--text-3)', padding: '4px 10px 5px' }}>{section.label}</div>
                )}
                {section.label && !showLabels && si > 0 && <div style={{ height: 1, background: 'var(--border)', margin: '6px 6px' }} />}
                {items.map((i) => <NavBtn key={i.id} item={i} />)}
              </div>
            );
          })}
        </nav>
        <div onClick={() => nav('/profile')} title="My profile" className="cc-hover-s2 cc-clickable" style={{ flex: '0 0 auto', borderTop: '1px solid var(--border)', padding: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
          <Avatar initials={avInitials} bg="var(--accent-bg)" fg="var(--accent)" size={32} />
          {showLabels && (
            <div style={{ minWidth: 0, flex: '1 1 auto' }}>
              <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.name || (loadingUser ? '…' : 'Guest')}</div>
              <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>{user ? ROLE_LABELS[user.role] : ''}</div>
            </div>
          )}
        </div>
      </aside>

      {/* Main column */}
      <div style={{ flex: '1 1 auto', minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <header style={{ height: 56, flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: 14, padding: '0 20px', borderBottom: '1px solid var(--border)', background: 'var(--surface)' }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 15.5, fontWeight: 700, letterSpacing: '-.01em', lineHeight: 1.1 }}>{title}</div>
            <div style={{ fontSize: 12, color: 'var(--text-3)', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{subtitle}</div>
          </div>
          <div style={{ flex: '1 1 auto' }} />
          <HeaderSearch />
          <button onClick={toggleDark} title="Toggle theme" className="cc-hover-s2" style={{ appearance: 'none', border: '1px solid var(--border)', background: 'var(--surface)', cursor: 'pointer', color: 'var(--text-2)', width: 34, height: 34, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon name={dark ? 'light_mode' : 'dark_mode'} size={20} />
          </button>
          <NotificationsBell />
        </header>

        <main style={{ flex: '1 1 auto', overflowY: 'auto', padding: 24, background: 'var(--bg)' }}>
          <div style={{ maxWidth: 1280, margin: '0 auto' }}>
            <Outlet />
          </div>
        </main>
      </div>

      <DrawerHost />
    </div>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)', color: 'var(--text-3)', fontFamily: "'Public Sans',system-ui,sans-serif", fontSize: 14 }}>
      {children}
    </div>
  );
}

function AuthError() {
  return (
    <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)', fontFamily: "'Public Sans',system-ui,sans-serif", padding: 24 }}>
      <div style={{ maxWidth: 460, textAlign: 'center', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 28, boxShadow: 'var(--shadow)' }}>
        <Icon name="lock" size={34} color="var(--text-3)" />
        <h2 style={{ margin: '12px 0 6px', fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>Couldn’t sign you in</h2>
        <p style={{ margin: '0 0 16px', fontSize: 13.5, color: 'var(--text-2)', lineHeight: 1.6 }}>
          The API returned <strong>401 Not authenticated</strong>. Either Cloudflare Access
          isn’t in front of this app yet, or open testing mode is off. Set
          <code style={{ fontFamily: "'JetBrains Mono',monospace", background: 'var(--surface-2)', padding: '1px 5px', borderRadius: 4 }}> OPEN_ACCESS=true</code> in
          the Pages environment variables (and ensure the <code style={{ fontFamily: "'JetBrains Mono',monospace", background: 'var(--surface-2)', padding: '1px 5px', borderRadius: 4 }}>DB</code> binding is attached), then redeploy.
        </p>
        <button onClick={() => window.location.reload()} style={{ appearance: 'none', border: 0, background: 'var(--accent)', color: '#fff', fontFamily: 'inherit', fontWeight: 600, fontSize: 13, padding: '9px 16px', borderRadius: 8, cursor: 'pointer' }}>
          Retry
        </button>
      </div>
    </div>
  );
}
